/**
 * GeoJSON(통계청 kostat/2012) 행정구역 코드 ↔ SGIS 현행 adm_cd 매핑.
 *
 * - 시도(2자리): 17개 코드가 SGIS 와 완전히 일치 → 항등.
 * - 시군구(5자리): 대부분 일치하나 2012 → 현행 행정구역 변경분(예: 청주시 통합,
 *   군위군 대구 편입)만 예외. 전면 매핑표 없이 불일치 코드만 아래 표로 보정한다.
 *   매칭 실패 코드는 결측(회색)으로 표시되므로, 운영 중 불일치가 확인되면 여기에 추가.
 */

/** GeoJSON 시군구 코드 → SGIS adm_cd 예외 보정 (규칙으로 못 잡는 불일치만 등록) */
const SIGUNGU_CODE_OVERRIDES: Record<string, string> = {
  '23030': '23090', // 인천 남구 → 미추홀구 (2018 개명, 코드도 변경)
  '37310': '22520', // 군위군 → 2023 대구 편입
};

/**
 * GeoJSON properties 코드를 SGIS adm_cd 로 변환.
 * SGIS 는 군(郡) 코드를 +200 대역으로 쓴다 (예: 연천군 31350→31550, 기장군 21310→21510) —
 * 끝 3자리 ≥310 이면 군이므로 +200. 전수 대조(2024년 252곳)로 검증된 규칙이며,
 * 예외는 override 로 처리한다. 부천시(31050)는 SGIS 가 3개 구로 나눠 유일하게 미매칭(결측).
 */
export function geoCodeToAdmCd(geoCode: string): string {
  const override = SIGUNGU_CODE_OVERRIDES[geoCode];
  if (override) return override;
  const suffix = Number(geoCode.slice(2));
  if (geoCode.length === 5 && suffix >= 310) return geoCode.slice(0, 2) + (suffix + 200);
  return geoCode;
}

/**
 * GeoJSON 시군구명 → opinet 시군구명 보정 (확정된 불일치만 등록).
 *
 * opinet 은 시군구를 "시 단위"로 저장하나, GeoJSON(통계청 2012)은 일반구 단위
 * ("성남시분당구") 또는 통합 이전 옛 구역명을 가져 정규식 축약만으로는 맞지 않는
 * 케이스가 있다. 정밀 대조로 도출한 다음 항목을 우선 적용한다.
 * 운영 중 추가 불일치가 발견되면 여기에 등록.
 *
 * 한계: 세종특별자치시는 GeoJSON/ opinet 표기 차이가 있을 수 있어 그대로 통과시키며,
 * 불일치 확인 시 override 에 추가한다.
 */
const GEO_SIGUNGU_OPINET_OVERRIDES: Record<string, string> = {
  마산합포구: '창원시',
  마산회원구: '창원시',
  성산구: '창원시',
  의창구: '창원시',
  진해구: '창원시',
  여주군: '여주시',
  청원군: '청주시',
};

/** GeoJSON 시군구명을 opinet 시군구명으로 변환 (override → 정규식 축약 → 항등) */
export function geoSigunguToOpinet(geoName: string): string {
  const override = GEO_SIGUNGU_OPINET_OVERRIDES[geoName];
  if (override) return override;

  // "○○시△△구" 형태(일반구)는 시 단위로 축약. 예: 성남시분당구 → 성남시
  const match = geoName.match(/^(.+시).+구$/);
  if (match) return match[1];

  return geoName;
}

/**
 * 클릭한 GeoJSON 구가 opinet 시(상위)의 일부라 좌표 필터가 필요한지 판정.
 *
 * opinet 은 일반구(분당구 등)·창원 통합구를 시 단위로만 저장하므로, 이런 구를 클릭하면
 * searchByRegion 은 시 전체를 반환한다. 좌표 기반 폴리곤 필터로 구 단위로 좁혀야 한다.
 *
 * - 일반구(성남시분당구→성남시)·창원 통합구(마산합포구→창원시): true
 * - 광역시 자치구(강남구, 불변)·군(여주군/청원군, 시 전체로 취급): false
 */
export function needsGuPolygonFilter(geoName: string): boolean {
  return /구$/.test(geoName) && geoSigunguToOpinet(geoName) !== geoName;
}

/**
 * GeoJSON 시군구 코드 → opinet 지역 보정 (코드 기준).
 *
 * 이름 기준 override 로는 안 되는 케이스만 등록한다 — '남구'는 부산·대구·울산·광주에도
 * 있어 이름 전역 치환이 불가능하고, 군위군은 시도 축까지 바뀐다.
 */
const GEO_CODE_REGION_OVERRIDES: Record<string, { sido?: string; sigungu: string }> = {
  '23030': { sigungu: '미추홀구' }, // 2018-07 인천 남구 → 미추홀구 개명
  '37011': { sigungu: '포항시' }, // GeoJSON 이름이 '남구'뿐이라 시 단위 축약 불가
  '37012': { sigungu: '포항시' }, // GeoJSON 이름이 '북구'뿐이라 시 단위 축약 불가
  '37310': { sido: '대구', sigungu: '군위군' }, // 2023-07 경북 군위군 → 대구 편입
};

/** GeoJSON 시군구 피처 → opinet 지역 키 "시도|시군구" (가격 히트맵용) */
export function geoSigunguRegionKey(code: string, parentCode: string, name: string): string {
  const ovr = GEO_CODE_REGION_OVERRIDES[code];
  const sido = ovr?.sido ?? geoSidoCodeToOpinet(parentCode);
  return `${sido}|${ovr?.sigungu ?? geoSigunguToOpinet(name)}`;
}

/** GeoJSON(kostat 2012) 시도 코드 2자리 → opinet(web.fuel_station_info.sido) 축약 표기 */
const SIDO_CODE_OPINET: Record<string, string> = {
  '11': '서울', '21': '부산', '22': '대구', '23': '인천', '24': '광주', '25': '대전', '26': '울산',
  '29': '세종시', '31': '경기', '32': '강원', '33': '충북', '34': '충남', '35': '전북', '36': '전남',
  '37': '경북', '38': '경남', '39': '제주',
};

export function geoSidoCodeToOpinet(code: string): string {
  return SIDO_CODE_OPINET[code] ?? code;
}
